import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Clock, Check, CheckCheck, AlertTriangle } from 'lucide-react';

type BadgeVariant = 'success' | 'destructive' | 'secondary' | 'info' | 'warning';

// Aircall's status enumeration is not publicly documented. Values observed in
// production logs: 'pending' (admin-staged), 'sent' (message.sent webhook),
// 'delivered' / 'undelivered' (message.status_updated). 'failed' / 'rejected'
// / 'error' are kept as defensive fallbacks.
const STATUS_CONFIG: Record<
  string,
  { label: string; variant: BadgeVariant; icon?: React.ComponentType<{ className?: string }> }
> = {
  pending:     { label: 'sending',     variant: 'secondary',   icon: Clock },
  sent:        { label: 'sent',        variant: 'secondary',   icon: Check },
  delivered:   { label: 'delivered',   variant: 'info',        icon: CheckCheck },
  undelivered: { label: 'undelivered', variant: 'destructive', icon: AlertTriangle },
  failed:      { label: 'failed',      variant: 'destructive', icon: AlertTriangle },
  rejected:    { label: 'rejected',    variant: 'destructive', icon: AlertTriangle },
  error:       { label: 'error',       variant: 'destructive', icon: AlertTriangle },
};

function formatTs(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function SmsDeliveryStatusBadge({ metadata }: { metadata?: Record<string, any> }) {
  const status = metadata?.delivery_status;
  if (!status) return null;

  const cfg = STATUS_CONFIG[String(status)] ?? { label: String(status), variant: 'secondary' as BadgeVariant };
  const Icon = cfg.icon;

  const tooltipLines = [
    metadata?.sent_at && `Sent: ${formatTs(metadata.sent_at)}`,
    metadata?.delivered_at && `Delivered: ${formatTs(metadata.delivered_at)}`,
    metadata?.undelivered_at && `Undelivered: ${formatTs(metadata.undelivered_at)}`,
    metadata?.failed_at && `Failed: ${formatTs(metadata.failed_at)}`,
  ].filter(Boolean) as string[];

  const badge = (
    <Badge variant={cfg.variant as any} className="text-[10px] px-1.5 py-0 gap-0.5">
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {cfg.label}
    </Badge>
  );

  if (tooltipLines.length === 0) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          {tooltipLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
